import {
	capabilityFor,
	offerableCodes,
	type PanelModel,
	type PanelProvider,
	publishObjection,
	unofferedAdapters,
	unofferedModels,
} from "@superadmin/utils/providers_panel";
import { Chip } from "@voicely/ui-mui/data-display/Chip";
import { Typography } from "@voicely/ui-mui/data-display/Typography";
import { Alert } from "@voicely/ui-mui/feedback/Alert";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@voicely/ui-mui/feedback/Dialog";
import { Button } from "@voicely/ui-mui/form/Button";
import { FormControl, FormControlLabel, InputLabel } from "@voicely/ui-mui/form/FormControl";
import { MenuItem } from "@voicely/ui-mui/form/MenuItem";
import { Select } from "@voicely/ui-mui/form/Select";
import { Switch } from "@voicely/ui-mui/form/Switch";
import { TextField } from "@voicely/ui-mui/form/TextField";
import { Box } from "@voicely/ui-mui/layout/Box";
import { Stack } from "@voicely/ui-mui/layout/Stack";
import type { ProviderKind } from "@voicely/zod-schemas/enums.zod";
import { describeLanguages } from "@voicely/zod-schemas/languages";
import type { ProviderCapabilityOffer } from "@voicely/zod-schemas/provider.zod";
import { useState } from "react";

/**
 * The four forms that write the catalogue.
 *
 * ## Why these are pickers and not text fields
 *
 * Every field a vendor or a model is identified by is a key in the runtime's
 * adapter registry, a model id spelled the vendor's way, or an ISO subtag the
 * adapter has been measured serving from India. All three are refused on save
 * when wrong, in sentences written to be read — and a refusal is a worse
 * interface than a list, because it arrives one field at a time and only after
 * somebody has typed four things from memory.
 *
 * So the forms are built from `providers.capabilities`, which is the SAME
 * snapshot the write path validates against. That is the point of reading it
 * rather than mirroring it: the menu and the check cannot disagree, because
 * they are the same source read twice in one request.
 *
 * Free text remains exactly where a human genuinely has something to say —
 * `label`, `description`, `sortOrder` — none of which the runtime has an
 * opinion about.
 *
 * ## Why a save can still be refused
 *
 * The forms narrow what can be *chosen*; they are not the boundary. A runtime
 * that redeployed between the page load and the save has changed what is
 * possible underneath a menu drawn from the old answer, and a runtime that
 * cannot be reached at all refuses the write outright rather than writing an
 * unchecked row. Both refusals are rendered verbatim, beside the form, because
 * both say what to do next.
 *
 * ## Why unsaved input is not in the URL
 *
 * The house rule puts user-visible state in the route, and explicitly exempts
 * half-typed form input: a URL lives in browser history, in server logs, and in
 * whatever gets pasted into a chat window. What IS in the route is which form
 * is open and what it is about — see the page.
 */

/** The refusal shape both write paths surface, as oRPC delivers it. */
export interface WriteError {
	message: string;
	/** Which field the server blamed, when it named one. */
	field?: string;
}

interface FormShellProps {
	title: string;
	/** What saving this would do, when that is not obvious from the title. */
	intro?: string;
	open: boolean;
	saving: boolean;
	error: WriteError | null;
	saveLabel: string;
	/** Disables save with a reason, for a form that is not complete yet. */
	blockedBecause?: string | null;
	onSave: () => void;
	onClose: () => void;
	children: React.ReactNode;
}

/**
 * The frame every form below is drawn in.
 *
 * Carries the refusal as well as the buttons, because a refusal that scrolls
 * away from the field it is about is a refusal nobody connects to what they
 * typed.
 */
function FormShell({
	title,
	intro,
	open,
	saving,
	error,
	saveLabel,
	blockedBecause,
	onSave,
	onClose,
	children,
}: FormShellProps) {
	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>{title}</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ pt: 1 }}>
					{intro && (
						<Typography variant="body2" color="text.secondary">
							{intro}
						</Typography>
					)}

					{/* Verbatim. "Nova 3 does not serve ta from inside India" is
					    written to be read by the person who chose it; paraphrasing
					    it into a validation code is how it stops being actionable. */}
					{error && <Alert severity="error">{error.message}</Alert>}

					{children}

					{blockedBecause && <Alert severity="warning">{blockedBecause}</Alert>}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={saving}>
					Cancel
				</Button>
				<Button variant="contained" onClick={onSave} disabled={saving || Boolean(blockedBecause)}>
					{saving ? "Saving…" : saveLabel}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

/** The leg names, spelled as the rest of the panel spells them. */
const KIND_LABEL: Record<ProviderKind, string> = {
	stt: "Speech to text",
	llm: "Inference",
	tts: "Synthesis",
};

export interface ProviderDraft {
	kind: ProviderKind;
	name: string;
	label: string;
	description: string | null;
	residency: string | null;
	sortOrder: number;
	published: boolean;
}

/**
 * Add a vendor — chosen from the adapters the runtime has built.
 *
 * The dropdown IS the interesting part of this form. An adapter with no row is
 * a vendor that exists in the runtime and is offered to nobody, which is a
 * deliberate state rather than an oversight, and until this endpoint existed
 * nothing outside the Elixir source could name one.
 *
 * `residency` is seeded from the adapter's own declaration and stays editable:
 * the adapter states where it answers from, and a super-admin may still need to
 * say "the country belongs to the model" by clearing it, which is what a
 * gateway fronting separately-deployed models requires.
 */
export function AddProviderForm({
	open,
	capabilities,
	providers,
	saving,
	error,
	onSave,
	onClose,
}: {
	open: boolean;
	capabilities: ProviderCapabilityOffer[];
	providers: PanelProvider[];
	saving: boolean;
	error: WriteError | null;
	onSave: (draft: ProviderDraft) => void;
	onClose: () => void;
}) {
	const available = unofferedAdapters(capabilities, providers);

	const [picked, setPicked] = useState("");
	const [label, setLabel] = useState("");
	const [description, setDescription] = useState("");
	const [residency, setResidency] = useState("");
	const [sortOrder, setSortOrder] = useState("0");

	const adapter = available.find((entry) => `${entry.kind}:${entry.provider}` === picked);

	const choose = (value: string) => {
		setPicked(value);

		// Seeded, not locked. The adapter knows where it answers from; the label
		// is a product decision and nobody but a person can write it.
		const next = available.find((entry) => `${entry.kind}:${entry.provider}` === value);
		setResidency(next?.residency ?? "");
	};

	return (
		<FormShell
			title="Add a vendor"
			intro="Only adapters built in the realtime runtime can be added. A vendor listed here exists and is offered to nobody — writing the adapter is the first step and this row is the second."
			open={open}
			saving={saving}
			error={error}
			saveLabel="Create as draft"
			blockedBecause={
				available.length === 0
					? "Every adapter the runtime declares already has a row. A new vendor starts in the runtime, not here."
					: !adapter
						? "Choose an adapter."
						: label.trim() === ""
							? "Give it a label — this is what a workspace reads instead of the registry key."
							: null
			}
			onSave={() =>
				adapter &&
				onSave({
					kind: adapter.kind,
					name: adapter.provider,
					label: label.trim(),
					description: description.trim() || null,
					residency: residency.trim() || null,
					sortOrder: Number(sortOrder) || 0,
					// Always a draft. Publishing is a separate, deliberate act on a
					// row somebody can already see — a vendor that goes live in the
					// same click that creates it has never been looked at.
					published: false,
				})
			}
			onClose={onClose}
		>
			<FormControl fullWidth>
				<InputLabel id="adapter">Adapter</InputLabel>
				<Select
					labelId="adapter"
					label="Adapter"
					value={picked}
					onChange={(event) => choose(String(event.target.value))}
				>
					{available.map((entry) => (
						<MenuItem
							key={`${entry.kind}:${entry.provider}`}
							value={`${entry.kind}:${entry.provider}`}
						>
							{entry.provider} — {KIND_LABEL[entry.kind]} ({entry.models.length} models)
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<TextField
				label="Label"
				value={label}
				onChange={(event) => setLabel(event.target.value)}
				helperText="What a workspace reads. The registry key above is what the runtime looks the adapter up by."
				fullWidth
			/>

			<TextField
				label="Description"
				value={description}
				onChange={(event) => setDescription(event.target.value)}
				multiline
				minRows={2}
				helperText="Why somebody would choose this over its siblings. Optional."
				fullWidth
			/>

			<TextField
				label="Residency"
				value={residency}
				onChange={(event) => setResidency(event.target.value)}
				helperText="The country the VENDOR answers from. Clear it when the country belongs to the model instead, which is what a gateway fronting separately-deployed models needs."
				fullWidth
			/>

			<TextField
				label="Sort order"
				value={sortOrder}
				onChange={(event) => setSortOrder(event.target.value)}
				helperText="Menu order within the leg, and the order the default lane prefers."
				fullWidth
			/>
		</FormShell>
	);
}

export interface ProviderPatch {
	label: string;
	description: string | null;
	residency: string | null;
	sortOrder: number;
	published: boolean;
}

/**
 * Edit a vendor.
 *
 * `kind` and `name` are shown and cannot be changed. Together they are the
 * identity every agent's `provider_profile` stores, so renaming one would
 * orphan every agent pinned to it — silently, because the agent row would still
 * parse. The server omits both from its update schema for the same reason; this
 * renders them as text so the rule is visible rather than merely enforced.
 */
export function EditProviderForm({
	open,
	provider,
	saving,
	error,
	onSave,
	onDelete,
	onClose,
}: {
	open: boolean;
	provider: PanelProvider;
	saving: boolean;
	error: WriteError | null;
	onSave: (patch: ProviderPatch) => void;
	onDelete: () => void;
	onClose: () => void;
}) {
	const [label, setLabel] = useState(provider.label);
	const [description, setDescription] = useState(provider.description ?? "");
	const [residency, setResidency] = useState(provider.residency ?? "");
	const [sortOrder, setSortOrder] = useState(String(provider.sortOrder ?? 0));
	const [published, setPublished] = useState(provider.published);

	return (
		<FormShell
			title={provider.label}
			open={open}
			saving={saving}
			error={error}
			saveLabel="Save"
			blockedBecause={label.trim() === "" ? "A vendor needs a label." : null}
			onSave={() =>
				onSave({
					label: label.trim(),
					description: description.trim() || null,
					residency: residency.trim() || null,
					sortOrder: Number(sortOrder) || 0,
					published,
				})
			}
			onClose={onClose}
		>
			<Stack direction="row" spacing={1} alignItems="center">
				<Chip size="small" label={provider.name} variant="outlined" />
				<Chip size="small" label={KIND_LABEL[provider.kind]} variant="outlined" />
				<Typography variant="caption" color="text.secondary">
					Fixed — together these are the identity every agent stores. A vendor under a new name is a
					new row.
				</Typography>
			</Stack>

			<TextField
				label="Label"
				value={label}
				onChange={(event) => setLabel(event.target.value)}
				fullWidth
			/>

			<TextField
				label="Description"
				value={description}
				onChange={(event) => setDescription(event.target.value)}
				multiline
				minRows={2}
				fullWidth
			/>

			<TextField
				label="Residency"
				value={residency}
				onChange={(event) => setResidency(event.target.value)}
				helperText="Empty means the country belongs to the model."
				fullWidth
			/>

			<TextField
				label="Sort order"
				value={sortOrder}
				onChange={(event) => setSortOrder(event.target.value)}
				fullWidth
			/>

			<FormControlLabel
				control={
					<Switch checked={published} onChange={(event) => setPublished(event.target.checked)} />
				}
				label="Published — workspaces may choose this vendor"
			/>

			<Box>
				<Button color="error" onClick={onDelete} disabled={saving}>
					Delete this vendor and its models
				</Button>
				<Typography variant="caption" color="text.secondary" display="block">
					The models go with it. There is nothing a model means without the vendor that serves it,
					and an orphan row would be selectable and unresolvable. Agents already pinned to it fail
					over rather than stop.
				</Typography>
			</Box>
		</FormShell>
	);
}

export interface ModelDraft {
	modelId: string;
	label: string;
	description: string | null;
	languages: string[];
	codeSwitching: string[];
	isDefault: boolean;
	sortOrder: number;
	published: boolean;
}

/**
 * The two language controls, capped at what the adapter declares.
 *
 * Multi-selects over the adapter's own sets rather than free text, which is the
 * subset rule stated as a menu: a language the adapter does not serve from
 * India is not a typo to be corrected after a round trip, it is a choice that
 * was never available.
 *
 * `codeSwitching` is capped again by what this row currently offers, because it
 * is a subset of `languages` too — a language the row does not serve singly
 * cannot be sold as half of a code-switched pair.
 */
function LanguageFields({
	available,
	languages,
	codeSwitching,
	onLanguages,
	onCodeSwitching,
}: {
	available: { languages: string[]; codeSwitching: string[] };
	languages: string[];
	codeSwitching: string[];
	onLanguages: (next: string[]) => void;
	onCodeSwitching: (next: string[]) => void;
}) {
	// Only what this row already serves. Kept in sync by the caller, which drops
	// a code from `codeSwitching` the moment it leaves `languages`.
	const switchable = available.codeSwitching.filter((code) => languages.includes(code));

	return (
		<>
			<FormControl fullWidth>
				<InputLabel id="languages">Languages served from India</InputLabel>
				<Select
					labelId="languages"
					label="Languages served from India"
					multiple
					value={languages}
					onChange={(event) => onLanguages(event.target.value as string[])}
					renderValue={(selected) => describeLanguages(selected as string[])}
				>
					{available.languages.map((code) => (
						<MenuItem key={code} value={code}>
							{code}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			{available.languages.length === 0 && (
				<Alert severity="warning">
					This adapter declares no language served from inside India for this model. That is a
					declaration rather than a gap — nothing has measured it — and a row narrowed from it can
					only ever serve nothing.
				</Alert>
			)}

			<FormControl fullWidth disabled={switchable.length === 0}>
				<InputLabel id="code-switching">Carried together on one socket</InputLabel>
				<Select
					labelId="code-switching"
					label="Carried together on one socket"
					multiple
					value={codeSwitching}
					onChange={(event) => onCodeSwitching(event.target.value as string[])}
					renderValue={(selected) => describeLanguages(selected as string[])}
				>
					{switchable.map((code) => (
						<MenuItem key={code} value={code}>
							{code}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<Typography variant="caption" color="text.secondary">
				{switchable.length === 0
					? "This model carries one language per socket. Empty is load-bearing: it is what keeps a bilingual line off a model whose multilingual mode routes abroad."
					: "What ONE socket carries at once — the claim a bilingual agent's dropdown is gated on. Distinct from the list above: a model can serve two languages separately and neither of them together."}
			</Typography>
		</>
	);
}

/** Add a model — chosen from the ids this vendor's adapter declares. */
export function AddModelForm({
	open,
	provider,
	capabilities,
	saving,
	error,
	onSave,
	onClose,
}: {
	open: boolean;
	provider: PanelProvider;
	capabilities: ProviderCapabilityOffer[];
	saving: boolean;
	error: WriteError | null;
	onSave: (draft: ModelDraft) => void;
	onClose: () => void;
}) {
	const capability = capabilityFor(capabilities, provider.kind, provider.name);
	const available = unofferedModels(capability, provider);

	const [modelId, setModelId] = useState("");
	const [label, setLabel] = useState("");
	const [description, setDescription] = useState("");
	const [languages, setLanguages] = useState<string[]>([]);
	const [codeSwitching, setCodeSwitching] = useState<string[]>([]);
	const [sortOrder, setSortOrder] = useState("0");

	const codes = offerableCodes(capability, modelId);

	const choose = (value: string) => {
		setModelId(value);

		// Seeded with the adapter's full declaration, because offering everything
		// the runtime can serve is the common case and narrowing is the deliberate
		// one. Both remain editable downwards.
		const next = offerableCodes(capability, value);
		setLanguages(next.languages);
		setCodeSwitching(next.codeSwitching);
	};

	return (
		<FormShell
			title={`Add a model to ${provider.label}`}
			intro="The ids are the adapter's own. A model the runtime has never heard of cannot open a socket, whatever the row says."
			open={open}
			saving={saving}
			error={error}
			saveLabel="Create as draft"
			blockedBecause={
				available.length === 0
					? "Every model this adapter declares already has a row. A new model starts in the runtime."
					: modelId === ""
						? "Choose a model."
						: label.trim() === ""
							? "Give it a label — this is what a workspace reads instead of the vendor's id."
							: null
			}
			onSave={() =>
				onSave({
					modelId,
					label: label.trim(),
					description: description.trim() || null,
					languages,
					codeSwitching,
					// Never on create. Which model is THE one for a leg is a decision
					// about a row somebody can already see, and a new row silently
					// taking over as the platform default is how a default moves
					// without anybody choosing it.
					isDefault: false,
					sortOrder: Number(sortOrder) || 0,
					published: false,
				})
			}
			onClose={onClose}
		>
			<FormControl fullWidth>
				<InputLabel id="model">Model</InputLabel>
				<Select
					labelId="model"
					label="Model"
					value={modelId}
					onChange={(event) => choose(String(event.target.value))}
				>
					{available.map((entry) => (
						<MenuItem key={entry.model} value={entry.model}>
							{entry.model}
							{entry.vendor ? ` — ${entry.vendor}` : ""}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<TextField
				label="Label"
				value={label}
				onChange={(event) => setLabel(event.target.value)}
				helperText='What a workspace reads — "Nova 3", not "deepgram/nova-3-general".'
				fullWidth
			/>

			<TextField
				label="Description"
				value={description}
				onChange={(event) => setDescription(event.target.value)}
				multiline
				minRows={2}
				fullWidth
			/>

			<LanguageFields
				available={codes}
				languages={languages}
				codeSwitching={codeSwitching}
				onLanguages={(next) => {
					setLanguages(next);
					// A code that leaves `languages` cannot stay in the pair claim,
					// which is a subset of it. Dropped here rather than refused on
					// save, because the server's message would be about a field the
					// person never touched.
					setCodeSwitching((current) => current.filter((code) => next.includes(code)));
				}}
				onCodeSwitching={setCodeSwitching}
			/>

			<TextField
				label="Sort order"
				value={sortOrder}
				onChange={(event) => setSortOrder(event.target.value)}
				fullWidth
			/>
		</FormShell>
	);
}

export interface ModelPatch {
	label: string;
	description: string | null;
	languages: string[];
	codeSwitching: string[];
	isDefault: boolean;
	sortOrder: number;
	published: boolean;
	retiresAt: number | null;
}

/**
 * Edit a model, including the retirement date.
 *
 * The date is the field worth reading the helper text for. Setting ANY date —
 * including one in the past, which is how a vendor's already-completed
 * withdrawal gets recorded — hides the model from every selection list
 * immediately. It looks like an off-by-one and is not: the date says when the
 * VENDOR stops serving it, and there is no reason to keep handing out something
 * known to be going away.
 */
export function EditModelForm({
	open,
	provider,
	model,
	capabilities,
	saving,
	error,
	usage,
	onSave,
	onDelete,
	onClose,
}: {
	open: boolean;
	provider: PanelProvider;
	model: PanelModel;
	capabilities: ProviderCapabilityOffer[];
	saving: boolean;
	error: WriteError | null;
	/** What retiring it would move. Null while the count is still in flight. */
	usage: { agentCount: number; templateCount: number } | null;
	onSave: (patch: ModelPatch) => void;
	onDelete: () => void;
	onClose: () => void;
}) {
	const capability = capabilityFor(capabilities, provider.kind, provider.name);
	const codes = offerableCodes(capability, model.modelId);

	const [label, setLabel] = useState(model.label);
	const [description, setDescription] = useState(model.description ?? "");
	const [languages, setLanguages] = useState<string[]>(model.languages);
	const [codeSwitching, setCodeSwitching] = useState<string[]>(model.codeSwitching ?? []);
	const [isDefault, setIsDefault] = useState(model.isDefault);
	const [sortOrder, setSortOrder] = useState(String(model.sortOrder ?? 0));
	const [published, setPublished] = useState(model.published);
	const [retiresAt, setRetiresAt] = useState(
		model.retiresAt === null ? "" : new Date(model.retiresAt).toISOString().slice(0, 10),
	);

	// Checked against what is ON SCREEN rather than what is stored: the question
	// is whether the save about to happen would offer anything, and the person
	// may have just emptied the language list.
	const objection = published ? publishObjection({ languages }, capability, model.modelId) : null;

	return (
		<FormShell
			title={model.label}
			open={open}
			saving={saving}
			error={error}
			saveLabel="Save"
			blockedBecause={label.trim() === "" ? "A model needs a label." : null}
			onSave={() =>
				onSave({
					label: label.trim(),
					description: description.trim() || null,
					languages,
					codeSwitching,
					isDefault,
					sortOrder: Number(sortOrder) || 0,
					published,
					retiresAt: retiresAt === "" ? null : Date.parse(`${retiresAt}T00:00:00Z`),
				})
			}
			onClose={onClose}
		>
			<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
				<Chip size="small" label={model.modelId} variant="outlined" />
				{model.vendor && (
					<Chip
						size="small"
						variant="outlined"
						label={model.vendorModel ? `${model.vendor} · ${model.vendorModel}` : model.vendor}
					/>
				)}
				<Typography variant="caption" color="text.secondary">
					The id is fixed — it is what every agent on this model stores.
				</Typography>
			</Stack>

			<TextField
				label="Label"
				value={label}
				onChange={(event) => setLabel(event.target.value)}
				fullWidth
			/>

			<TextField
				label="Description"
				value={description}
				onChange={(event) => setDescription(event.target.value)}
				multiline
				minRows={2}
				fullWidth
			/>

			<LanguageFields
				available={codes}
				languages={languages}
				codeSwitching={codeSwitching}
				onLanguages={(next) => {
					setLanguages(next);
					setCodeSwitching((current) => current.filter((code) => next.includes(code)));
				}}
				onCodeSwitching={setCodeSwitching}
			/>

			<FormControlLabel
				control={
					<Switch checked={isDefault} onChange={(event) => setIsDefault(event.target.checked)} />
				}
				label="Default for this vendor — preferred over catalogue order"
			/>

			<TextField
				label="Sort order"
				value={sortOrder}
				onChange={(event) => setSortOrder(event.target.value)}
				fullWidth
			/>

			<FormControlLabel
				control={
					<Switch checked={published} onChange={(event) => setPublished(event.target.checked)} />
				}
				label="Published — workspaces may choose this model"
			/>

			{/* Legal narrowing, offered to nobody. A subset check cannot object
			    to this — `[] ⊆ anything` — so the panel does. */}
			{objection && <Alert severity="warning">{objection}</Alert>}

			<TextField
				label="Retires on"
				type="date"
				value={retiresAt}
				onChange={(event) => setRetiresAt(event.target.value)}
				slotProps={{ inputLabel: { shrink: true } }}
				helperText="Setting any date hides this from every selection list IMMEDIATELY, not on the date. The date is when the vendor stops serving it; agents already on it keep working until then and fail over after. Set it earlier than the vendor's own — that gap is the only migration window anybody gets."
				fullWidth
			/>

			{usage && (usage.agentCount > 0 || usage.templateCount > 0) && (
				<Alert severity="warning">
					{usage.agentCount} agents and {usage.templateCount} platform templates name this model. A
					template counts for more than an agent even though the number is smaller: an agent on a
					dead model is a migration with a known end, while a template keeps minting new agents onto
					it until the template is changed too.
				</Alert>
			)}

			<Box>
				<Button color="error" onClick={onDelete} disabled={saving}>
					Delete this model
				</Button>
				<Typography variant="caption" color="text.secondary" display="block">
					Deleting is not retiring. A retirement date leaves the row in place so agents pinned to it
					keep working and then fail over; deleting removes it, and an agent naming a model that is
					not there falls down the vendor's ladder instead. Retire it if anything is still on it.
				</Typography>
			</Box>
		</FormShell>
	);
}
